import {AxiosResponse} from 'axios';
import {action, observable, runInAction} from 'mobx';

import {I18nApi} from '@udemy/i18n';
import {udSentry} from '@udemy/sentry';
import {udApi} from '@udemy/ud-api';

export const POPULAR_TOPICS = 'popular_topics';
export const POPULAR_TOPICS_IN_CATEGORY = 'popular_topics_in_category';
export const GLOBAL_STATS_BASE_URL = 'structured-data/tags/topic/global-stats/';
export const SD_TAG_BASE_URL = 'structured-data/tags/';
export const ROLE_MARKETPLACE = 'marketplace';
export const DEFAULT_STAT_SIZE = 100;

const fetchPopularTopics = async () => {
    const response: AxiosResponse = await udApi.get(GLOBAL_STATS_BASE_URL, {
        params: {
            stat_types: POPULAR_TOPICS,
            stat_size: DEFAULT_STAT_SIZE,
            stat_role: ROLE_MARKETPLACE,
        },
    });
    return response.data.results;
};

const fetchPopularTopicsByCategory = async (sdTagId?: number) => {
    const response: AxiosResponse = await udApi.get(`${SD_TAG_BASE_URL}category/${sdTagId}/`, {
        params: {
            'fields[course_category]': 'stats',
            stat_types: POPULAR_TOPICS_IN_CATEGORY,
            stat_size: DEFAULT_STAT_SIZE,
            stat_role: ROLE_MARKETPLACE,
        },
    });
    return response.data.stats;
};

export class TopicListCollectionStore {
    constructor(gettext: I18nApi['gettext']) {
        this.topicTabs = [
            {
                tabName: gettext('Bestselling'),
                store: new TopicListStore(POPULAR_TOPICS),
            },
            {
                tabName: gettext('Development'),
                store: new TopicListStore(POPULAR_TOPICS_IN_CATEGORY, 288),
            },
            {
                tabName: gettext('Design'),
                store: new TopicListStore(POPULAR_TOPICS_IN_CATEGORY, 269),
            },
            {
                tabName: gettext('Business'),
                store: new TopicListStore(POPULAR_TOPICS_IN_CATEGORY, 268),
            },
            {
                tabName: gettext('Personal Development'),
                store: new TopicListStore(POPULAR_TOPICS_IN_CATEGORY, 296),
            },
        ];
    }

    topicTabs: Array<{
        tabName: string;
        store: TopicListStore;
    }>;

    @observable activeTabId = 0;

    // used for mobile pagination
    @action
    updateTopicTable(tabId: number) {
        this.activeTabId = tabId;
    }
}

export interface TopicItem {
    _class: string;
    id: number;
    title: string;
    url: string;
    icon_class: string;
    type: string;
}

export type TopicItems = Array<{
    sd_tag: TopicItem;
    inner_sd_tag: TopicItem;
}>;

export class TopicListStore {
    constructor(statType: string, sdTagId?: number) {
        this.statType = statType;
        this.sdTagId = sdTagId;
    }

    statType: string;
    sdTagId?: number;
    pageSize = 10;
    @observable page = 1;
    @observable isLoading = true;
    @observable items: TopicItems = [];
    @observable hasMore = false;

    @action
    loadTopics = async (pageSize: number) => {
        runInAction(() => {
            this.isLoading = true;
        });
        this.pageSize = pageSize;
        let allItems: TopicItems;
        try {
            if (this.statType === POPULAR_TOPICS_IN_CATEGORY) {
                allItems = await fetchPopularTopicsByCategory(this.sdTagId);
            } else {
                allItems = await fetchPopularTopics();
            }
            runInAction(() => {
                if (allItems) {
                    this.items?.push(
                        ...allItems.slice(this.items.length, this.items.length + this.pageSize),
                    );
                    this.hasMore = this.items.length < allItems.length;
                }
            });
        } catch (error) {
            udSentry.captureException(error);
        }
        runInAction(() => {
            this.isLoading = false;
        });
    };
}
